import type { NewMeetingForm, NewMeetingState } from "@bundle/core";
import { Button, Icon, Menu, Sheet, Stack, Text, TextField, Toggle, type MenuItem } from "../../primitives/index.ts";
import { useT, useTr } from "../../i18n/I18nProvider.tsx";

export type NewMeetingSheetProps = {
  /** Open while defined. */
  form: NewMeetingState | undefined;
  /** Conversations I can put the meeting in ("# design", "Bo"). */
  conversations: { id: string; label: string }[];
  canCreate: boolean;
  /** "Link copied" shows on the copy button. */
  copied: boolean;
  onEdit: (patch: Partial<NewMeetingForm>) => void;
  onCreate: () => void;
  onCopy: (url: string) => void;
  onStart: () => void;
  onClose: () => void;
  testID: string;
};

/**
 * "New external huddle": a title, an optional conversation (members follow it there), now or later, and whether guests
 * may join without asking; then the link to share, and "Start meeting" for one made for now.
 */
export function NewMeetingSheet(p: NewMeetingSheetProps) {
  const t = useT();
  const tr = useTr();
  const form = p.form;
  const created = form?.created;
  const conversation = p.conversations.find((c) => c.id === form?.channelId);
  const pick: MenuItem[] = [
    { id: "none", label: t("meeting.new.noConversation"), ...(form?.channelId === undefined ? { icon: "check" as const } : {}), onSelect: () => p.onEdit({ channelId: undefined }) },
    ...p.conversations.map((c) => ({ id: `c-${c.id}`, label: c.label, ...(c.id === form?.channelId ? { icon: "check" as const } : {}), onSelect: () => p.onEdit({ channelId: c.id }) })),
  ];
  return (
    <Sheet open={form !== undefined} onClose={p.onClose} title={t("meeting.new.title")} closeLabel={t("common.close")} detents={["medium"]} testID={p.testID}>
      {form === undefined ? null : created === undefined ? (
        <Stack gap="4" paddingY="1">
          <TextField label={t("meeting.new.name")} value={form.title} onChange={(title) => p.onEdit({ title })} placeholder={t("meeting.new.namePlaceholder")} autoFocus onSubmit={p.onCreate} testID={`${p.testID}-title`} />
          <Stack gap="1.5">
            <Text variant="footnote" color="text.secondary">
              {t("meeting.new.conversation")}
            </Text>
            <Stack direction="horizontal">
              <Menu items={pick} testID={`${p.testID}-conversations`}>
                <Button label={conversation?.label ?? t("meeting.new.noConversation")} icon="message" onPress={() => undefined} testID={`${p.testID}-conversation`} />
              </Menu>
            </Stack>
          </Stack>
          <Stack direction="horizontal" gap="2">
            <Button label={t("meeting.new.now")} variant={form.when === "now" ? "primary" : "secondary"} onPress={() => p.onEdit({ when: "now" })} testID={`${p.testID}-now`} />
            <Button label={t("meeting.new.later")} icon="calendar" variant={form.when === "later" ? "primary" : "secondary"} onPress={() => p.onEdit({ when: "later" })} testID={`${p.testID}-later`} />
          </Stack>
          {form.when === "later" ? (
            <TextField label={t("meeting.new.time")} value={form.time} onChange={(time) => p.onEdit({ time })} placeholder="14:30" testID={`${p.testID}-time`} />
          ) : null}
          <Toggle label={t("huddle.meeting.openToAll")} value={form.open} onChange={(open) => p.onEdit({ open })} testID={`${p.testID}-open`} />
          {form.error === undefined ? null : (
            <Text variant="footnote" color="status.danger" testID={`${p.testID}-error`}>
              {tr(form.error)}
            </Text>
          )}
          <Stack direction="horizontal" justify="end">
            <Button label={t("meeting.new.create")} icon="link" variant="primary" disabled={!p.canCreate} loading={form.busy} onPress={p.onCreate} testID={`${p.testID}-create`} />
          </Stack>
        </Stack>
      ) : (
        <Stack gap="4" paddingY="1" testID={`${p.testID}-ready`}>
          <Stack direction="horizontal" align="center" gap="2">
            <Icon name="checkCircle" size="icon.md" color="status.success" />
            <Stack grow>
              <Text>{t("meeting.new.ready")}</Text>
            </Stack>
          </Stack>
          <Stack direction="horizontal" align="center" gap="2" padding="2.5" radius="md" background="chip.bg">
            <Stack grow>
              <Text mono selectable lines={1} testID={`${p.testID}-url`}>
                {created.url}
              </Text>
            </Stack>
            <Button label={p.copied ? t("huddle.share.copied") : t("huddle.share.copy")} icon={p.copied ? "check" : "copy"} size="sm" onPress={() => p.onCopy(created.url)} testID={`${p.testID}-copy`} />
          </Stack>
          {created.scheduledAt === undefined ? (
            <Stack direction="horizontal" justify="end">
              <Button label={t("meeting.new.start")} icon="huddle" variant="primary" onPress={p.onStart} testID={`${p.testID}-start`} />
            </Stack>
          ) : null}
        </Stack>
      )}
    </Sheet>
  );
}
